import React from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { useTranslation } from 'react-i18next'
import { Edit, Calendar, Building2, User, FileText, Trash2 } from 'lucide-react'
import { AssetListItem } from '@/modules/Asset/services'
import AssetStatusBadge from '@/modules/Asset/components/AssetStatusBadge'
import dayjs from 'dayjs'

interface DraftAssetCardProps {
    asset: AssetListItem
    onEdit: (asset: AssetListItem) => void
    onDelete: (asset: AssetListItem) => void
}

const DraftAssetCard: React.FC<DraftAssetCardProps> = ({
    asset,
    onEdit,
    onDelete,
}) => {
    const { t } = useTranslation('asset')
    const version = asset.current_version

    return (
        <div className="flex flex-col h-full bg-white border border-gray-200/80 rounded-xl hover:border-primary/30 hover:shadow-sm transition-all">
            {/* Header */}
            <div className="p-4 pb-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                    <h3
                        className="font-semibold text-base text-gray-900 line-clamp-2"
                        title={asset.asset_name}
                    >
                        {asset.asset_name || (
                            <span className="text-gray-400 italic">
                                {t('form.notProvided')}
                            </span>
                        )}
                    </h3>
                    <AssetStatusBadge
                        status={asset.status || 'DRAFT'}
                        className="text-xs shrink-0"
                    />
                </div>

                {/* Badges */}
                <div className="flex flex-wrap items-center gap-1.5">
                    {asset.asset_slug && (
                        <Badge variant="outline" className="text-xs font-mono">
                            {asset.asset_slug}
                        </Badge>
                    )}
                    {version?.document_type && (
                        <Badge variant="outline" className="text-xs font-mono">
                            {t(`document_types.${version.document_type}`)}
                        </Badge>
                    )}
                </div>
            </div>

            {/* Description */}
            <div className="px-4 pb-3 flex-1">
                <p
                    className="text-sm text-gray-500 line-clamp-2"
                    title={asset.asset_description}
                >
                    {asset.asset_description || t('form.notProvided')}
                </p>
            </div>

            <Separator />

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-gray-500">
                <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" />
                    <span>{asset.created_by_name || '-'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" />
                    <span>{asset.department_name || '-'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>
                        {asset.updated_at
                            ? dayjs(asset.updated_at).format('DD/MM/YYYY')
                            : '-'}
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" />
                    <span>
                        {asset.total_files || 0} {t('form.files')}
                    </span>
                </div>
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex items-center gap-2 p-3">
                <Button
                    variant="default"
                    size="sm"
                    className="flex-1 gap-2"
                    onClick={() => onEdit(asset)}
                >
                    <Edit className="w-4 h-4" />
                    {t('actions.update')}
                </Button>
                <Button
                    variant="destructive"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => onDelete(asset)}
                    title={t('actions.delete')}
                >
                    <Trash2 className="w-4 h-4" />
                </Button>
            </div>
        </div>
    )
}

export default DraftAssetCard
